import {
  AGE_LADDER,
  AGE_NO_CHANGE,
  COBRA_RULES,
  FAMILY_RULES,
  MEDICARE_RULES,
  TIMELINE,
  TRAINING_RULES,
  TRAINING_STATES,
  UNEMPLOYMENT_LEVELS,
} from '@/content/severance-atlas/coverage'
import { STATES, countWhere } from '@/content/severance-atlas/model'
import { BASE_EMPLOYEE, CHECKLIST, type View } from '@/content/severance-atlas/perspective'
import { useEffect, useState } from 'react'
import {
  Callout,
  ConfidenceTag,
  HEAT,
  RuleGrid,
  StateChip,
  SubHead,
  cardClass,
  pip,
  scrollToId,
} from './primitives'

const CHECKS_KEY = 'pelgo-severance-atlas-checks'

const readChecks = (): Record<string, boolean> => {
  try {
    return JSON.parse(window.localStorage.getItem(CHECKS_KEY) ?? '{}') ?? {}
  } catch {
    return {}
  }
}

export function ViewSwitch({ view, onChange }: { view: View; onChange: (v: View) => void }) {
  const options: { v: View; title: string; sub: string }[] = [
    { v: 'er', title: 'Employer / HR view', sub: 'What you must do, and by when' },
    { v: 'ee', title: 'Employee view', sub: 'What you’re owed, and what to check' },
  ]
  return (
    <fieldset className="m-0 mt-8 inline-grid min-w-0 grid-cols-2 overflow-hidden rounded-2xl border border-gray-200 bg-white p-1 shadow-sm">
      <legend className="sr-only">Choose a perspective</legend>
      {options.map((o) => (
        <button
          key={o.v}
          type="button"
          aria-pressed={view === o.v}
          onClick={() => onChange(o.v)}
          className={`grid gap-0.5 rounded-xl px-4 py-3 text-left transition-colors md:px-6 ${
            view === o.v ? 'bg-pelgo-blue-dark text-white' : 'text-gray-900 hover:bg-gray-50'
          }`}
        >
          <span className="text-sm font-semibold md:text-base">{o.title}</span>
          <span className={`text-xs ${view === o.v ? 'text-white/75' : 'text-gray-500'}`}>
            {o.sub}
          </span>
        </button>
      ))}
    </fieldset>
  )
}

export function Checklist({ view }: { view: View }) {
  // Hydrate after mount so prerendered HTML matches the first client render.
  const [checked, setChecked] = useState<Record<string, boolean>>({})
  useEffect(() => setChecked(readChecks()), [])

  const toggle = (key: string, on: boolean) => {
    setChecked((prev) => {
      const next = { ...prev, [key]: on }
      try {
        window.localStorage.setItem(CHECKS_KEY, JSON.stringify(next))
      } catch {
        // Storage can be unavailable (private mode); ticks just won't persist.
      }
      return next
    })
  }

  return (
    <ul className="m-0 grid list-none gap-3 p-0 md:grid-cols-2">
      {CHECKLIST[view].map(([title, detail, target], i) => {
        const key = `${view}${i}`
        const on = Boolean(checked[key])
        return (
          <li
            key={key}
            className={`${cardClass} grid grid-cols-[22px_1fr] items-start gap-3 p-5 transition-opacity ${
              on ? 'opacity-60' : ''
            }`}
          >
            <input
              id={`sev-check-${key}`}
              type="checkbox"
              checked={on}
              onChange={(e) => toggle(key, e.target.checked)}
              className="mt-1 h-4 w-4 accent-pelgo-blue-dark"
            />
            <div className="space-y-1">
              <label
                htmlFor={`sev-check-${key}`}
                className="block cursor-pointer text-base font-display font-medium text-gray-900"
              >
                {title}
              </label>
              <p className="m-0 text-sm text-gray-500">
                {detail}{' '}
                <button
                  type="button"
                  onClick={() => scrollToId(target)}
                  className="font-medium text-[#0050e6] hover:underline"
                >
                  Details
                </button>
              </p>
            </div>
          </li>
        )
      })}
    </ul>
  )
}

export function EmployeeBaseline() {
  return <RuleGrid rules={BASE_EMPLOYEE} />
}

export function HealthSection() {
  return (
    <>
      <SubHead>What happens after the last day, in order</SubHead>
      <ol className="m-0 ml-2 list-none border-l-2 border-gray-200 p-0">
        {TIMELINE.map((t) => (
          <li key={t.what} className="relative max-w-3xl pb-6 pl-6 last:pb-0">
            <span
              aria-hidden
              className={`absolute -left-[7px] top-1.5 h-3 w-3 rounded-full border-2 ${
                t.warn ? 'border-red-600 bg-red-600' : 'border-[#0050e6] bg-white'
              }`}
            />
            <p
              className={`m-0 text-xs font-semibold uppercase tracking-wide ${
                t.warn ? 'text-red-700' : 'text-[#0050e6]'
              }`}
            >
              {t.when}
            </p>
            <p className="m-0 mt-1 text-base font-display font-medium text-gray-900">{t.what}</p>
            <p className="m-0 mt-1 text-sm text-gray-500">{t.why}</p>
          </li>
        ))}
      </ol>

      <SubHead>Federal COBRA: what applies everywhere</SubHead>
      <RuleGrid rules={COBRA_RULES} />

      <SubHead>State continuation (“mini-COBRA”) by state</SubHead>
      <Callout>
        <b className="font-semibold text-gray-800">How reliable this data is.</b> No official
        50-state source exists. Two aggregators were compared, and every state where they disagreed
        was checked against the statute or state agency. Wherever it was checked, CobraHelp
        understated or invented coverage and FindCoverage was right. State laws usually apply to
        insured plans of employers with fewer than 20 employees, since self-funded plans fall under
        federal law (ERISA) instead. Also on the explorer map as the “State continuation” lens.
      </Callout>
      <div className={`${cardClass} overflow-x-auto`}>
        <table className="w-full min-w-[820px] border-collapse text-sm">
          <thead>
            <tr>
              {['State', 'Law', 'Who it covers', 'Max duration', 'Notable rules', 'Confidence'].map(
                (h) => (
                  <th
                    key={h}
                    scope="col"
                    className="whitespace-nowrap border-b border-gray-200 bg-gray-50 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                  >
                    {h}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {STATES.map((s) => (
              <tr key={s.c}>
                <td className="whitespace-nowrap border-b border-gray-100 px-3 py-2.5 align-top font-semibold text-gray-900">
                  {s.n}
                </td>
                <td className="whitespace-nowrap border-b border-gray-100 px-3 py-2.5 align-top text-gray-700">
                  {pip(s.mc[0])}
                  {s.mc[1]}
                </td>
                <td className="border-b border-gray-100 px-3 py-2.5 align-top text-gray-700">
                  {s.mc[2]}
                </td>
                <td className="border-b border-gray-100 px-3 py-2.5 align-top text-gray-700">
                  {s.mc[3]}
                </td>
                <td className="border-b border-gray-100 px-3 py-2.5 align-top text-gray-500">
                  {s.mc[4] || '-'}
                </td>
                <td className="border-b border-gray-100 px-3 py-2.5 align-top">
                  <ConfidenceTag c={s.mc[5]} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <SubHead>Medicare and individual insurance</SubHead>
      <RuleGrid rules={MEDICARE_RULES} />
    </>
  )
}

export function UnemploymentSection() {
  return (
    <>
      <Callout>
        <b className="font-semibold text-gray-800">How reliable this data is.</b> The main 50-state
        source is OkayNext’s guide. Where it and SeveranceCalc’s guide disagreed (Illinois,
        Virginia, Georgia, Montana), the state agency or a state law firm sided with OkayNext each
        time. Verified means checked with a state source or two agreeing guides; 1 source means
        OkayNext only. Unemployment rules also depend on facts such as whether severance is
        “allocated” to specific weeks, so the state agency’s decision is what counts. Also on the
        explorer map as the “Severance vs. unemployment” lens.
      </Callout>
      <ul className="m-0 mb-4 flex list-none flex-wrap gap-x-4 gap-y-2 p-0 text-xs text-gray-700">
        {[3, 2, 1, 0].map((v) => (
          <li key={v} className="inline-flex items-center gap-1.5">
            <i className={`inline-block h-3 w-3 rounded-sm ${HEAT[v]}`} />
            {UNEMPLOYMENT_LEVELS[v]}
            <span className="text-gray-400">{countWhere((s) => s.ui[0] === v)}</span>
          </li>
        ))}
      </ul>
      <div className={`${cardClass} overflow-x-auto`}>
        <table className="w-full min-w-[720px] border-collapse text-sm">
          <thead>
            <tr>
              {['State', 'Effect', 'Rule', 'Confidence'].map((h) => (
                <th
                  key={h}
                  scope="col"
                  className="whitespace-nowrap border-b border-gray-200 bg-gray-50 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {STATES.map((s) => (
              <tr key={s.c}>
                <td className="whitespace-nowrap border-b border-gray-100 px-3 py-2.5 align-top font-semibold text-gray-900">
                  {s.n}
                </td>
                <td className="whitespace-nowrap border-b border-gray-100 px-3 py-2.5 align-top text-gray-700">
                  {pip(s.ui[0])}
                  {UNEMPLOYMENT_LEVELS[s.ui[0]]}
                </td>
                <td className="border-b border-gray-100 px-3 py-2.5 align-top text-gray-700">
                  {s.ui[1]}
                </td>
                <td className="border-b border-gray-100 px-3 py-2.5 align-top">
                  <ConfidenceTag c={s.ui[2]} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

export function AgeSection() {
  return (
    <>
      <div className="grid gap-3">
        {AGE_LADDER.map((r) => (
          <div
            key={r.age}
            className={`${cardClass} grid items-start gap-2 p-5 sm:grid-cols-[96px_1fr] sm:gap-6`}
          >
            <p className="m-0 text-4xl font-display font-medium leading-none tracking-[-0.8px] text-pelgo-blue-dark">
              {r.age}
              <span className="mt-1 block text-xs font-medium tracking-normal text-gray-400">
                years
              </span>
            </p>
            <div>
              <h3 className="mb-2 text-lg font-display font-medium tracking-[-0.3px] text-gray-900">
                {r.h}
              </h3>
              <ul className="m-0 list-disc space-y-1.5 pl-5 text-sm text-gray-700">
                {r.pts.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>
      <p className="mt-4 rounded-2xl border border-dashed border-gray-200 p-4 text-sm text-gray-600">
        <b className="font-semibold text-gray-800">What age does not change:</b> {AGE_NO_CHANGE}
      </p>
    </>
  )
}

export function FamilySection() {
  return <RuleGrid rules={FAMILY_RULES} />
}

export function RetrainingSection({ onOpen }: { onOpen: (code: string) => void }) {
  return (
    <>
      <RuleGrid rules={TRAINING_RULES} />
      <SubHead>States with their own programs or extra benefits</SubHead>
      <div className="grid gap-4 md:grid-cols-2">
        {TRAINING_STATES.map((g) => (
          <article key={g.h} className={`${cardClass} grid content-start gap-3 p-6`}>
            <h3 className="flex items-baseline justify-between gap-3 text-base font-display font-medium tracking-[-0.2px] text-gray-900">
              {g.h}
              <span className="shrink-0 text-xs font-medium text-gray-400">
                {g.list.length} states
              </span>
            </h3>
            <ul className="m-0 grid list-none gap-2 p-0">
              {g.list.map(([code, text]) => (
                <li
                  key={code + text}
                  className="grid grid-cols-[38px_1fr] items-baseline gap-2 text-sm text-gray-800"
                >
                  <StateChip code={code} onOpen={onOpen} />
                  <span>{text}</span>
                </li>
              ))}
            </ul>
          </article>
        ))}
      </div>
    </>
  )
}
